import { useCallback, useEffect, useRef, useState } from 'react'
import { Dayjs } from 'dayjs'
import { DateUtils } from 'src/service/utils/date'

type useCountdownProps = {
  countExpiredCallbackFn: () => void
}

type CountdownControllers = {
  count: string
  countStart: (timeout: number) => void
  countStop: () => void
  originCount: number
}

export const useCountdown = ({
  countExpiredCallbackFn,
}: useCountdownProps): CountdownControllers => {
  const { now, getDayDifference, timeFormatter } = DateUtils
  const [count, setCount] = useState<number>(0)
  const [endTime, setEndTime] = useState<Dayjs | null>(null)

  const countStart = useCallback(
    (timeout: number) => {
      setCount(timeout)
      setEndTime(now().add(timeout * 1000, 'milliseconds'))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [count],
  )
  const countStop = () => {
    setEndTime(null)
    setCount(0)
  }

  useInterval(
    useCallback(() => {
      if (count < 1) {
        countExpiredCallbackFn?.()
        countStop()
        return
      }
      setCount(Math.ceil(Number(getDayDifference(endTime!, 'milliseconds')) / 1000))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, countStop]),
    endTime ? 1000 : null,
  )

  return {
    count: timeFormatter(count),
    countStop,
    countStart,
    originCount: count,
  }
}

function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback)

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    if (delay === null) {
      return
    }

    const id = setInterval(() => {
      savedCallback.current()
    }, delay)

    return () => {
      clearInterval(id)
    }
  }, [delay])
}
